import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { filter, map } from 'rxjs';

export interface SeoRouteData {
  title?: string;
  description?: string;
  keywords?: string;
  robots?: string;
  image?: string;
  type?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly siteName = 'Swipfx';
  private readonly defaultTitle = 'Swipfx | Digital Asset Trading and Payments';
  private readonly defaultDescription =
    'Swipfx helps users trade, deposit, withdraw, and manage digital assets with secure account and compliance workflows.';
  private readonly defaultRobots = 'noindex, nofollow';

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly title: Title,
    private readonly meta: Meta,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  init(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => this.getDeepestRoute(this.activatedRoute)),
        map(route => route.snapshot.data?.['seo'] as SeoRouteData | undefined)
      )
      .subscribe(seo => this.apply(seo || {}));
  }

  private apply(seo: SeoRouteData): void {
    const title = seo.title ? `${seo.title} | ${this.siteName}` : this.defaultTitle;
    const description = seo.description || this.defaultDescription;
    const robots = seo.robots || this.defaultRobots;
    const url = this.getCanonicalUrl();
    const image = seo.image ? this.toAbsoluteUrl(seo.image) : '';
    const type = seo.type || 'website';

    this.title.setTitle(title);

    this.updateNameTag('description', description);
    this.updateNameTag('robots', robots);
    this.updateNameTag('application-name', this.siteName);
    this.updateNameTag('theme-color', '#0d6efd');

    if (seo.keywords) {
      this.updateNameTag('keywords', seo.keywords);
    } else {
      this.meta.removeTag("name='keywords'");
    }

    this.updatePropertyTag('og:site_name', this.siteName);
    this.updatePropertyTag('og:title', title);
    this.updatePropertyTag('og:description', description);
    this.updatePropertyTag('og:type', type);
    this.updatePropertyTag('og:url', url);

    this.updateNameTag('twitter:card', image ? 'summary_large_image' : 'summary');
    this.updateNameTag('twitter:title', title);
    this.updateNameTag('twitter:description', description);

    if (image) {
      this.updatePropertyTag('og:image', image);
      this.updateNameTag('twitter:image', image);
    } else {
      this.meta.removeTag("property='og:image'");
      this.meta.removeTag("name='twitter:image'");
    }

    this.updateCanonical(url);
  }

  private getDeepestRoute(route: ActivatedRoute): ActivatedRoute {
    let current = route;
    while (current.firstChild) {
      current = current.firstChild;
    }
    return current;
  }

  private getCanonicalUrl(): string {
    const origin = this.document.defaultView?.location.origin || 'https://swipfx.com';
    const path = this.router.url.split('?')[0].split('#')[0] || '/';
    return `${origin}${path}`;
  }

  private toAbsoluteUrl(value: string): string {
    if (/^https?:\/\//i.test(value)) return value;
    const origin = this.document.defaultView?.location.origin || 'https://swipfx.com';
    return `${origin}${value.startsWith('/') ? value : `/${value}`}`;
  }

  private updateNameTag(name: string, content: string): void {
    this.meta.updateTag({ name, content });
  }

  private updatePropertyTag(property: string, content: string): void {
    this.meta.updateTag({ property, content });
  }

  private updateCanonical(url: string): void {
    let link = this.document.querySelector<HTMLLinkElement>("link[rel='canonical']");

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }
}
